import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router";

import { AccessNotProvisioned, SignedOut } from "../features/auth/PublicPages";
import { AdminPage } from "../features/admin/AdminPage";
import { AssetLibraryPage } from "../features/assets/AssetLibraryPage";
import { DashboardPage } from "../features/dashboard/DashboardPage";
import { EditingJobPage, EditorPage } from "../features/editor/EditorPages";
import { HonestPage } from "../features/honest/HonestPage";
import { KaryaPage } from "../features/karya/KaryaPage";
import { StrategyWorkspacePage } from "../features/karya/StrategyWorkspacePage";
import { NotificationsPage } from "../features/notifications/NotificationsPage";
import { PackagePage } from "../features/packages/PackagePage";
import { PlanPage } from "../features/plans/PlanPage";
import { JobDetailPage } from "../features/production/JobDetailPage";
import { ProductionBoardPage } from "../features/production/ProductionBoardPage";
import { ProductsPage } from "../features/products/ProductsPage";
import { ProjectOverviewPage } from "../features/projects/ProjectOverviewPage";
import { ProjectsPage } from "../features/projects/ProjectsPage";
import { RequestDetailPage } from "../features/requests/RequestDetailPage";
import { ReviewQueuePage, ReviewWorkspacePage } from "../features/reviews/ReviewPages";
import { RequestsPage } from "../features/requests/RequestsPage";
import { SettingsPage } from "../features/settings/SettingsPage";
import { StudioPage } from "../features/studio/StudioPage";
import { ApiError } from "../lib/api";
import { isUnauthenticated, redirectToLogin, SessionProvider, useMeQuery } from "../lib/session";
import { AppShell } from "./AppShell";
import { ALL_NAV_ITEMS } from "./navigation";

function AuthGate() {
  const { t } = useTranslation();
  const me = useMeQuery();
  useEffect(() => {
    if (me.error && isUnauthenticated(me.error)) redirectToLogin();
  }, [me.error]);
  if (me.data) {
    return (
      <SessionProvider me={me.data}>
        <Outlet />
      </SessionProvider>
    );
  }
  if (me.error && !isUnauthenticated(me.error)) {
    return (
      <main className="flex h-full items-center justify-center p-8 text-center">
        <div>
          <h1 className="text-[22px] font-semibold text-ink">{t("error.page_title")}</h1>
          <p className="mt-2 text-ink-2">{t("error.NETWORK")}</p>
          <button type="button" className="mt-6 text-violet underline" onClick={() => void me.refetch()}>
            {t("common.retry")}
          </button>
        </div>
      </main>
    );
  }
  return (
    <main className="flex h-full items-center justify-center text-ink-2" aria-busy="true">
      {t("auth.signing_in")}
    </main>
  );
}

function NotFound() {
  const { t } = useTranslation();
  return <h1 className="text-[28px] font-semibold text-ink">{t("error.not_found_title")}</h1>;
}

const honestRoutes = ALL_NAV_ITEMS.filter((n) => n.path !== "/" && !n.live).map((item) => ({
  path: item.path.slice(1),
  element: <HonestPage item={item} />,
}));

const router = createBrowserRouter([
  { path: "/access-not-provisioned", element: <AccessNotProvisioned /> },
  { path: "/signed-out", element: <SignedOut /> },
  {
    element: <AuthGate />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: "requests", element: <RequestsPage /> },
          { path: "requests/:displayId", element: <RequestDetailPage /> },
          { path: "projects", element: <ProjectsPage /> },
          { path: "projects/:displayId", element: <ProjectOverviewPage /> },
          { path: "projects/:displayId/strategy", element: <StrategyWorkspacePage /> },
          { path: "projects/:displayId/plan", element: <PlanPage /> },
          { path: "projects/:displayId/package", element: <PackagePage /> },
          { path: "projects/:displayId/reviews/:jobId", element: <ReviewWorkspacePage /> },
          { path: "reviews", element: <ReviewQueuePage /> },
          { path: "production", element: <ProductionBoardPage /> },
          { path: "production/jobs/:displayId", element: <JobDetailPage /> },
          { path: "shooting", element: <ProductionBoardPage preset={{ job_type: "SHOOTING", titleKey: "production.shooting_title", subtitleKey: "production.shooting_subtitle" }} /> },
          { path: "assets", element: <AssetLibraryPage /> },
          { path: "editor", element: <EditorPage /> },
          { path: "editor/jobs/:displayId", element: <EditingJobPage /> },
          { path: "studio/image", element: <StudioPage studio="image" /> },
          { path: "studio/video", element: <StudioPage studio="video" /> },
          { path: "karya", element: <KaryaPage /> },
          ...honestRoutes,
          { path: "notifications", element: <NotificationsPage /> },
          { path: "products", element: <ProductsPage /> },
          { path: "admin", element: <AdminPage /> },
          { path: "settings", element: <SettingsPage /> },
          { path: "*", element: <NotFound /> },
        ],
      },
    ],
  },
]);

// A 4xx is an answer (not found, forbidden, invalid state), not a transient failure: never retry it.
const retry = (count: number, error: unknown) =>
  !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2;
const queryClient = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false, retry } } });

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
